// Fetching, and the three states every screen has.
//
// There is no cache library here and no store. Every screen fetches what it
// shows when it mounts and refetches when a write succeeds, which at this size
// is the honest arrangement: the largest collection is 1,824 rows over a
// substring-searchable API, and a cache would be a second copy of the truth to
// keep current for no measurable gain.

import { useCallback, useEffect, useRef, useState } from 'react';
import * as api from './api';
import { COLLECTIONS, REFERENCES } from './schema';
import type { BarrierViewName, BarrierViewRow, DirectoryPerson, Me, Row } from './types';

export interface Async<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
  /** Refetch. Every write path calls it, so a screen shows what the database
   *  now holds rather than what the browser guessed it would hold. */
  reload: () => void;
}

/** `enabled` false holds off the fetch and reports loading until it is true. */
export function useAsync<T>(run: () => Promise<T>, deps: unknown[], enabled = true): Async<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  // A screen can be left before its fetch resolves -- every dashboard pane can
  // -- and setting state on an unmounted component is a warning at best and a
  // stale render at worst.
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  // Each fetch is numbered, and only the latest one's answer is kept. A grid's
  // filter can change faster than the API answers, and a slow answer to the
  // filter just left must not replace a quick one to the filter now shown.
  const latest = useRef(0);

  useEffect(() => {
    const ticket = ++latest.current;
    setLoading(true);
    // Not yet able to say what to fetch -- a filter waiting on who is signed
    // in. Loading rather than empty, so nothing reads as "no rows".
    if (!enabled) return;
    const current = () => live.current && ticket === latest.current;
    run()
      .then((value) => {
        if (!current()) return;
        setData(value);
        setError(null);
      })
      .catch((caught: Error) => {
        if (!current()) return;
        setError(caught);
      })
      .finally(() => {
        if (current()) setLoading(false);
      });
    // `run` is rebuilt on every render by every caller, so the dependency list
    // is the caller's own and `run` is deliberately not in it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload };
}

/** One barrier view, narrowed by the search box and by Action Owner.
 *
 *  `actionOwner` is an object id, `null` for everyone, or `undefined` while it
 *  cannot be known yet -- `owner=me` before `/api/me` has answered. Nothing is
 *  fetched until it can be, because fetching everyone first would show the
 *  wrong rows and then replace them. The API does the narrowing, so a grid's
 *  groups and its count are of the matching barriers only.
 *
 *  There is no default: a default parameter applies to `undefined` too, which
 *  would turn "not known yet" into "everyone". */
export function useBarrierView(
  view: BarrierViewName,
  search: string,
  actionOwner: string | null | undefined
): Async<BarrierViewRow[]> {
  return useAsync(
    () => {
      const params: Record<string, string> = {};
      if (search) params.q = search;
      if (actionOwner) params.action_owner_object_id = actionOwner;
      return api.allBarriers(view, Object.keys(params).length ? params : undefined);
    },
    [view, search, actionOwner],
    actionOwner !== undefined
  );
}

/** One person in the directory, by object id, or nothing for `null`. */
export function usePerson(objectId: string | null): Async<DirectoryPerson | null> {
  return useAsync(() => (objectId ? api.getPerson(objectId) : Promise.resolve(null)), [objectId]);
}

export function useCollection(slug: string, params?: Record<string, string | number>): Async<Row[]> {
  const key = JSON.stringify(params ?? {});
  return useAsync(() => api.allRows(slug, params), [slug, key]);
}

export function useMe(): Async<Me> {
  return useAsync(() => api.me(), []);
}

/** Every reference table a screen needs, as id -> label, fetched once.
 *
 *  Forms offer a list of plants and of products; a grid over a raw collection
 *  shows `product_id` as a number unless something resolves it. The four barrier
 *  views resolve their own names server-side, which is why they do not use this.
 */
export type Lookup = Map<number, string>;

export function useLookups(columns: readonly string[]): Async<Record<string, Lookup>> {
  const key = columns.join(',');
  return useAsync(async () => {
    const wanted = columns.map((c) => REFERENCES[c]).filter((r): r is NonNullable<typeof r> => Boolean(r));
    const unique = new Map(wanted.map((r) => [`${r.collection}:${r.display}`, r]));
    const resolved: Record<string, Lookup> = {};
    await Promise.all(
      [...unique.values()].map(async (reference) => {
        const rows = await api.allRows(reference.collection);
        const pk = collectionKey(reference.collection);
        const map: Lookup = new Map();
        for (const row of rows) {
          const id = row[pk];
          if (typeof id === 'number') map.set(id, String(row[reference.display] ?? ''));
        }
        for (const column of columns) {
          const candidate = REFERENCES[column];
          if (candidate && candidate.collection === reference.collection) resolved[column] = map;
        }
      })
    );
    return resolved;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

/** Each collection's key column, which `schema.ts` is the one place to hold. */
const KEYS: Record<string, string> = Object.fromEntries(
  Object.values(COLLECTIONS).map((c) => [c.slug, c.pk])
);

function collectionKey(slug: string): string {
  return KEYS[slug] ?? 'id';
}
